/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
} from "@material-ui/core";
import ButtonTS from "components/Forms/CustomButtons/ButtonTS";
import ReactTable from "components/Tables/ReactTable/ReactTable";
import React, { useEffect, useState } from "react";
import { FormControl, FormLabel } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSubmitBulkUpload } from "services/input-core/external-data/submit-massive-upload";
import { validatorSchema } from "./validatior";

interface Props extends DialogProps {
  onSuccess: () => void;
  onFail: () => void;
}

const UploadModal: React.FC<Props> = ({ onSuccess, onFail, ...props }) => {
  const { t } = useTranslation("bulk-upload");
  const [csvData, setCsvData] = useState([]);
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(validatorSchema),
    criteriaMode: "all",
    mode: "onChange",
  });
  const {
    mutate: sendCsvData,
    isLoading: isSendingCsvData,
  } = useSubmitBulkUpload();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSubmit = (data: any) => {
    sendCsvData(csvData, {
      onSuccess: () => {
        onSuccess();
        props.onClose && props.onClose({}, "backdropClick");
      },
      onError: () => {
        onFail();
        props.onClose && props.onClose({}, "backdropClick");
      },
    });
  };

  useEffect(() => {
    const file = watch("file");
    if (file && !errors.file) {
      validatorSchema.shape.file.parseAsync(file).then((file: any) => {
        setCsvData(file);
      });
    }
  }, [errors?.file, watch("file")]);

  return (
    <Dialog
      {...props}
      fullWidth
      maxWidth="lg"
      onBackdropClick={() => {
        reset();
        setCsvData([]);
        props.onClose && props.onClose({}, "backdropClick");
      }}
    >
      <DialogTitle>{t("upload-title-modal")}</DialogTitle>

      <DialogContent style={{ padding: "30px" }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="file"
            control={control}
            render={({ field: { onChange, onBlur, ref } }) => {
              return (
                <FormControl
                  ref={ref}
                  type="file"
                  accept="text/csv"
                  id="uploadInputFileCustom"
                  placeholder="aqui"
                  title={t("upload-title-button")}
                  style={{
                    width: "50%",
                    borderColor: errors?.file?.message ? "red" : "",
                    color: csvData.length > 0 ? "inherit" : "transparent",
                  }}
                  onClick={(e: any) => {
                    e.target = null;
                    setCsvData([]);
                    reset();
                  }}
                  onChange={(e: any) => {
                    if (e.target.files.length > 0) {
                      onChange(e.target.files[0]);
                    } else {
                      setCsvData([]);
                    }
                  }}
                  onBlur={onBlur}
                />
              );
            }}
          />

          <p style={{ color: "red" }}>
            {t(errors?.file?.message ? (errors.file.message as string) : "")}
          </p>

          <FormLabel style={{ paddingTop: "8px" }}>{t("upload-tip")}</FormLabel>

          <h4 style={{ marginTop: "30px", fontWeight: "bold", opacity: 0.6 }}>
            {t("preview-data")}
          </h4>
          <ReactTable
            initialPageSize={5}
            columns={[
              {
                Header: t("view-data.header.workerId"),
                accessor: "workerID",
              },
              {
                Header: t("view-data.header.documentType"),
                accessor: "documentType",
              },
              {
                Header: t("view-data.header.documentNumber"),
                accessor: "documentNumber",
              },
              {
                Header: t("view-data.header.documentSecurityNumber"),
                accessor: "documentSecurityNumber",
              },
              {
                Header: "",
                accessor: "test",
                width: 0,
              },
            ]}
            data={csvData || []}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              marginTop: "48px",
            }}
          >
            <ButtonTS
              simple
              color="transparent"
              onClick={() => {
                reset();
                setCsvData([]);
                props.onClose && props.onClose({}, "backdropClick");
              }}
            >
              {t("actions.cancel")}
            </ButtonTS>
            <ButtonTS
              type="submit"
              color="info"
              disabled={isSendingCsvData || !!errors?.file}
            >
              {t("actions.submit")}
            </ButtonTS>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UploadModal;
